<template>
  <div class="search-element">
    <div class="search-element__wrapper">
      <div class="search-element__title">
        {{ part.name }}
      </div>
      <img
        v-if="workMode.isModeSollers && imageUrl"
        class="search-element__image"
        :src="imageUrl"
        alt="part"
      >
      <div class="search-element__info">
        {{ $t('common.part_code') }}:
        <span class="search-element__info_name">{{ part.code }}</span>
      </div>
      <div class="search-element__info">
        {{ $t('common.kind') }}:
        <span :class="kindClass">{{ part.kind }}</span>
      </div>
      <p class="search-element__description">
        {{ part.description }}
      </p>
      <div class="search-element__bottom">
        <span class="search-element__info">
          {{ $t('views.parts_search.balance') }}
          <span class="search-element__info_name">
            {{ part.currentBalance | numberFormatter }}
          </span>
        </span>
        <span class="search-element__price">
          {{ part.recommendedRetailPrice | numberFormatter }}
          <span class="rouble">i</span>
        </span>
      </div>
    </div>

    <div>
      <hr class="search-element__hr">
      <div class="search-element__controls">
        <template v-if="canBeAddedToCart">
          <div class="search-element__count">
            <input
              v-if="cartItem"
              v-model.number="cartItem.quantity"
              type="number"
              min="1"
              readonly
              class="search-element__count__number"
            >
            <template v-else>
              <input
                v-model.number.lazy="quantity"
                type="number"
                min="1"
                class="search-element__count__number"
              >
              <div class="search-element__count__controls">
                <svg
                  width="16"
                  height="9"
                  viewBox="0 0 16 9"
                  @click="incrementQuantity"
                >
                  <path d="M8,0l8,9H0Z" />
                </svg>
                <svg
                  width="16"
                  height="9"
                  viewBox="0 0 16 9"
                  @click="decrementQuantity"
                >
                  <path
                    d="M8,0l8,9H0Z"
                    transform="translate(16 9) rotate(180)"
                  />
                </svg>
              </div>
            </template>
          </div>
          <div class="search-element__total">
            <span class="search-element__info">
              {{ $t('views.parts_search.sum') }}
              <span class="search-element__info_name">
                {{ total | numberFormatter }}
                <span class="rouble">i</span>
              </span>
            </span>
          </div>
          <div class="search-element__basket">
            <!-- eslint-disable max-len -->
            <button
              v-if="cartItem"
              class="btn spdo-button spdo-button_line spdo-button_lowercase search-element__button-in-cart"
              @click="$router.push('cart')"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
              >
                <path
                  d="M4.6,11.6a1.2,1.2,0,1,0,1.2,1.2A1.2,1.2,0,0,0,4.6,11.6ZM1,2V3.2H2.2L4.36,7.754l-.81,1.47A1.16,1.16,0,0,0,3.4,9.8,1.2,1.2,0,0,0,4.6,11h7.2V9.8H4.852a.149.149,0,0,1-.15-.15l.018-.072L5.26,8.6H9.73a1.194,1.194,0,0,0,1.05-.618l2.148-3.894A.586.586,0,0,0,13,3.8a.6.6,0,0,0-.6-.6H3.526L2.962,2H1Zm9.6,9.6a1.2,1.2,0,1,0,1.2,1.2A1.2,1.2,0,0,0,10.6,11.6Z"
                  transform="translate(-1 -2)"
                />
              </svg>
              {{ $t('views.parts_search.in_cart') }}
            </button>
            <button
              v-else
              class="btn spdo-button spdo-button_line spdo-button_lowercase search-element__button"
              @click="addToCart"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
              >
                <!-- eslint-disable max-len -->
                <path
                  d="M4.6,11.6a1.2,1.2,0,1,0,1.2,1.2A1.2,1.2,0,0,0,4.6,11.6ZM1,2V3.2H2.2L4.36,7.754l-.81,1.47A1.16,1.16,0,0,0,3.4,9.8,1.2,1.2,0,0,0,4.6,11h7.2V9.8H4.852a.149.149,0,0,1-.15-.15l.018-.072L5.26,8.6H9.73a1.194,1.194,0,0,0,1.05-.618l2.148-3.894A.586.586,0,0,0,13,3.8a.6.6,0,0,0-.6-.6H3.526L2.962,2H1Zm9.6,9.6a1.2,1.2,0,1,0,1.2,1.2A1.2,1.2,0,0,0,10.6,11.6Z"
                  transform="translate(-1 -2)"
                />
                <!-- eslint-enable max-len -->
              </svg>
              {{ $t('views.parts_search.add_to_cart') }}
            </button>
            <!-- eslint-enable max-len -->
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'PartCard',

  props: {
    part: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      quantity: 1,
      imageUrl: null,
    };
  },

  computed: {
    ...mapState(['cart', 'workMode']),

    cartItem() {
      return this.cart.find((position) => position.code === this.part.code);
    },

    total() {
      const quantity = this.cartItem ? this.cartItem.quantity : this.quantity;
      return quantity * this.part.recommendedRetailPrice;
    },

    canBeAddedToCart() {
      return this.workMode.isModeSollers || (!this.cart.length || this.cart[0].kind === this.part.kind);
    },

    kindClass() {
      return this.canBeAddedToCart ? 'part-card__kind_ok' : 'part-card__kind_nok';
    },
  },

  watch: {
    quantity(val, oldVal) {
      if (!Number.isInteger(val) || val <= 0) {
        this.quantity = oldVal;
      }
    },
  },

  created() {
    this.getImageUrl();
  },

  methods: {
    addToCart() {
      this.$store.commit('addToCart', { ...this.part, quantity: this.quantity });
    },

    incrementQuantity() {
      this.quantity += 1;
    },

    decrementQuantity() {
      if (this.quantity > 1) {
        this.quantity -= 1;
      }
    },

    async getImageUrl() {
      const { image, imageGUID } = this.part;

      if (image && image.filePath) {
        this.imageUrl = image.filePath;
        return;
      }

      const guid = imageGUID && imageGUID.length ? imageGUID : '00000000-0000-0000-0000-000000000000';
      const params = { operation: 'read' };
      const { data } = await this.$http.get(`/containers/part-images/${guid}/path`, { params });
      this.imageUrl = data.path;
    },

  },
};
</script>

<style lang="scss">
.search-element {
  padding: 40px 0;
  border-top: 3px solid var(--aurum);
  border-radius: 3px 3px 0 0;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;

  &__title {
    font-size: 26px;
    font-weight: 500;
    color: var(--topaz);
    margin-bottom: 25px;
  }

  &__info {
    color: var(--faint);
    margin-bottom: 25px;

    &_name {
      color: var(--topaz);
    }
  }

  &__description {
    color: #707070;
    line-height: 21px;
    margin-bottom: 25px !important;
  }

  &__price {
    color: var(--topaz);
    font-size: 26px;
    font-weight: 500;
  }

  &__hr {
    width: 100%;
    border-top: 3px solid #f9f7f6;
    margin-top: 25px;
    margin-bottom: 25px;
  }

  &__controls,
  &__bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .search-element__info {
      margin-bottom: 0;
    }
  }

  &__controls {
    height: 50px;
  }

  &__count {
    display: grid;
    grid-template-columns: 60px auto;
    align-items: center;
    column-gap: 15px;

    &__number {
      width: 60px;
      height: 40px;
      border: 1px solid #ddd;
      border-radius: 3px;
      padding: 0 10px;
      transition: border-color 0.3s ease-in-out;
      -moz-appearance: textfield;

      &:hover {
        border-color: var(--margaritas)
      }

      &:focus {
        outline: none;
        border-color: var(--aurum);
      }

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    }

    &__controls {
      display: grid;
      row-gap: 6px;

      svg {
        cursor: pointer;
        fill: var(--topaz);
        user-select: none;
        transition: opacity 0.3s ease-in-out;

        &:hover {
          opacity: 0.8;
        }

        &:active {
          opacity: 1;
        }
      }
    }
  }

  &__button {
    padding: 12px 20px;

    svg {
      fill: var(--black);
      transition: fill 0.3s ease-in-out;
    }

    &:hover {
      svg {
        fill: var(--aurum);
      }
    }

    &:active {
      svg {
        fill: #9d6d4c;
      }
    }
  }

  &__button-in-cart {
    padding: 10px 15px;
    white-space: nowrap;
    color: var(--aurum);

    svg {
      fill: var(--aurum);
      transition: fill 0.3s ease-in-out;
    }

    &:hover {
      color: var(--margaritas);

      svg {
        fill: var(--margaritas);
      }
    }

    &:active {
      color: var(--aurum);

      svg {
        fill: var(--aurum);
      }
    }
  }

  &__wrapper,
  &__controls {
    padding: 0 40px;
  }

  &__image {
    max-width: 250px;
    float: left;
    padding-right: 40px;
  }
}

.part-card__kind_ok {
  color: var(--sapphirus);
}

.part-card__kind_nok {
  color: var(--ruby);
}
</style>
